import { fak } from '@awesome.me/kit-da59d5031a/icons';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import {
    faCapsules,
    faChessKnight,
    faDog,
    faGlasses,
    faHandDots,
    faHandHoldingHeart,
    faHeadSideBrain,
    faHeartPulse,
    faMoneyCheckDollarPen,
    faPersonCane,
    faPersonRunningFast,
    faShieldHeart,
    faToothbrush,
    faVanShuttle,
} from '@fortawesome/pro-light-svg-icons';

import sortBy from 'lodash/sortBy';
import { CareProvided } from 'src/store/sageCareServiceApi';

export interface ResolutionTypeCategory {
    id: string;
    displayName?: string;
    sortOrder: number;
    subTypes?: ResolutionType[];
}
export interface ResolutionType {
    icon?: IconProp;
    id: CareProvided;
    displayName: string;
    sortOrder: number;
    category?: ResolutionTypeCategory;
}

export const ResolutionTypeCategories: Record<string, ResolutionTypeCategory> = {
    ADLHelp: {
        id: 'NON_EMERGENCY_ADL_HELP',
        displayName: 'ADL Help',
        sortOrder: 1,
    },
    Safety: {
        id: 'NON_EMERGENCY_SAFETY',
        displayName: 'Safety',
        sortOrder: 2,
    },
    IADLHelp: {
        id: 'NON_EMERGENCY_IADL_HELP',
        displayName: 'IADL Help',
        sortOrder: 3,
    },
    Request: {
        id: 'NON_EMERGENCY_REQUEST',
        displayName: 'Request',
        sortOrder: 4,
    },
    Emergency: {
        id: 'EMERGENCY',
        displayName: 'Emergency',
        sortOrder: 5,
    },
    Cleaning: {
        id: 'NON_EMERGENCY_HOUSEKEEPING',
        displayName: 'Cleaning',
        sortOrder: 6,
    },
    Others: {
        id: 'NON_EMERGENCY_OTHER',
        displayName: 'Others',
        sortOrder: 7,
    },
};

export const ResolutionTypes: Record<string, ResolutionType> = {
    Fall: {
        icon: fak.faFall,
        id: 'EMERGENCY_FALL',
        displayName: 'Fall',
        sortOrder: 1,
        category: ResolutionTypeCategories.Emergency,
    },
    /**
     * NOTE: this resolution type is currently only used in Proactive Fall Risk resolution.
     * However, one frequent request has been to specify this as the resolution instead of
     * "Fall", and only allow "Fall" selection after a review.
     */
    SeenOnFloor: {
        icon: fak.faFall,
        id: 'EMERGENCY_SEEN_ON_FLOOR',
        displayName: 'Seen on Floor',
        sortOrder: 1,
        // TODO: no category for the time being, but maybe be swapped with "Fall" later
        // category: ResolutionTypeCategories.Emergency,
    },
    OtherEmergency: {
        icon: fak.faEmergency,
        id: 'EMERGENCY_OTHER',
        displayName: 'Other Emergency',
        sortOrder: 2,
        category: ResolutionTypeCategories.Emergency,
    },
    Toileting: {
        icon: fak.faToileting,
        id: 'ADL_TOILETING',
        displayName: 'Toileting',
        sortOrder: 3,
        category: ResolutionTypeCategories.ADLHelp,
    },
    RoutineCheck: {
        icon: fak.faIconRoutineCheck,
        id: 'ADL_ROUTINE_CHECK',
        displayName: 'Routine Check',
        sortOrder: 4,
        category: ResolutionTypeCategories.ADLHelp,
    },
    Escort: {
        icon: fak.faEscort,
        id: 'ADL_ESCORT',
        displayName: 'Escort',
        sortOrder: 5,
        category: ResolutionTypeCategories.ADLHelp,
    },
    Dressing: {
        icon: fak.faDressing,
        id: 'ADL_DRESSING',
        displayName: 'Dressing',
        sortOrder: 6,
        category: ResolutionTypeCategories.ADLHelp,
    },
    Transfer: {
        icon: fak.faTransfer,
        id: 'ADL_TRANSFER',
        displayName: 'Transfer',
        sortOrder: 7,
        category: ResolutionTypeCategories.ADLHelp,
    },
    EatingFeeding: {
        icon: fak.faEating,
        id: 'ADL_EATING',
        displayName: 'Eating / Feeding',
        sortOrder: 8,
        category: ResolutionTypeCategories.ADLHelp,
    },
    Bathing: {
        icon: fak.faBathing,
        id: 'ADL_BATHING',
        displayName: 'Bathing',
        sortOrder: 9,
        category: ResolutionTypeCategories.ADLHelp,
    },
    Grooming: {
        icon: fak.faGrooming,
        id: 'ADL_GROOMING',
        displayName: 'Grooming',
        sortOrder: 10,
        category: ResolutionTypeCategories.ADLHelp,
    },
    Medtech: {
        icon: fak.faMedtech,
        id: 'REQUEST_MEDTECH',
        displayName: 'Medtech',
        sortOrder: 11,
        category: ResolutionTypeCategories.Request,
    },
    Oxygen: {
        icon: fak.faOxygen,
        id: 'REQUEST_OXYGEN',
        displayName: 'Oxygen',
        sortOrder: 12,
        category: ResolutionTypeCategories.Request,
    },
    Repair: {
        icon: fak.faRepair,
        id: 'REQUEST_REPAIR',
        displayName: 'Repair',
        sortOrder: 13,
        category: ResolutionTypeCategories.Request,
    },
    OtherRequest: {
        icon: fak.faOtherRequest,
        id: 'REQUEST_OTHER',
        displayName: 'Other Request',
        sortOrder: 14,
        category: ResolutionTypeCategories.Request,
    },
    EmotionalSupport: {
        icon: fak.faEmotionalSupport,
        id: 'REQUEST_EMOTIONAL_SUPPORT',
        displayName: 'Emotional Support',
        sortOrder: 15,
        category: ResolutionTypeCategories.Request,
    },
    Maintenance: {
        icon: fak.faRepair,
        id: 'REQUEST_MAINTENANCE',
        displayName: 'Maintenance',
        sortOrder: 16,
        category: ResolutionTypeCategories.Request,
    },
    HousekeepingUnspecified: {
        icon: fak.faHousekeeping,
        id: 'HOUSEKEEPING_UNSPECIFIED',
        displayName: 'Housekeeping',
        sortOrder: 17,
        category: ResolutionTypeCategories.Cleaning,
    },
    EmptiedTrash: {
        icon: fak.faEmptiedTrash,
        id: 'HOUSEKEEPING_TRASH',
        displayName: 'Emptied Trash',
        sortOrder: 18,
        category: ResolutionTypeCategories.Cleaning,
    },
    MadeBed: {
        icon: fak.faMadeBed,
        id: 'HOUSEKEEPING_BED',
        displayName: 'Made Bed',
        sortOrder: 19,
        category: ResolutionTypeCategories.Cleaning,
    },
    Laundry: {
        icon: fak.faLaundry,
        id: 'HOUSEKEEPING_LAUNDRY',
        displayName: 'Laundry',
        sortOrder: 20,
        category: ResolutionTypeCategories.Cleaning,
    },
    CleanedBathroom: {
        icon: fak.faCleanedBathroom,
        id: 'HOUSEKEEPING_BATHROOM',
        displayName: 'Cleaned Bathroom',
        sortOrder: 21,
        category: ResolutionTypeCategories.Cleaning,
    },
    OtherCleaning: {
        icon: fak.faOtherRequest,
        id: 'HOUSEKEEPING_OTHER',
        displayName: 'Other Cleaning',
        sortOrder: 22,
        category: ResolutionTypeCategories.Cleaning,
    },
    AccidentallyPressed: {
        icon: fak.faAccidentlyPressed,
        id: 'NON_EMERGENCY_ACCIDENTAL_PRESS',
        displayName: 'Accidentally Pressed',
        sortOrder: 23,
        category: ResolutionTypeCategories.Others,
    },
    Question: {
        icon: fak.faQuestion,
        id: 'NON_EMERGENCY_QUESTION',
        displayName: 'Question',
        sortOrder: 24,
        category: ResolutionTypeCategories.Others,
    },
    NotFeelingWell: {
        icon: fak.faNotFeelingWell,
        id: 'NON_EMERGENCY_ILLNESS',
        displayName: 'Not Feeling Well',
        sortOrder: 25,
        category: ResolutionTypeCategories.Others,
    },
    FoundDevice: {
        id: 'NON_EMERGENCY_FOUND_DEVICE',
        displayName: 'Test Alert',
        sortOrder: 26,
    },
    NoCareGiven: {
        id: 'NON_EMERGENCY_NO_CARE_GIVEN',
        displayName: 'No Care Given',
        sortOrder: 27,
    },
};

export const ResolutionCategoriesSorted = (() => {
    const groupedByCategories = Object.values(ResolutionTypes).reduce(
        (categories, resolutionType) => {
            if (
                resolutionType.id === 'REQUEST_EMOTIONAL_SUPPORT' ||
                resolutionType.id === 'REQUEST_MAINTENANCE' ||
                resolutionType.id === 'HOUSEKEEPING_UNSPECIFIED'
            ) {
                return categories; // Skip SNF specific outcomes
            }
            const category = resolutionType.category;
            if (category) {
                // Add category to map if it doesn't exist
                if (!categories[category.id]) {
                    // eslint-disable-next-line no-param-reassign
                    categories[category.id] = {
                        ...category,
                        subTypes: [],
                    };
                }
                categories[category.id].subTypes!.push(resolutionType);
            }
            return categories;
        },
        {} as Record<string, ResolutionTypeCategory>
    );

    // Return sorted array of categories with sorted subtypes
    return sortBy(
        Object.values(groupedByCategories).map((category) => ({
            ...category,
            subTypes: sortBy(category.subTypes, 'sortOrder'),
        })),
        'sortOrder'
    );
})();

export const IndependentLivingResolutions: ResolutionTypeCategory[] = [
    {
        ...ResolutionTypeCategories.Emergency,
        subTypes: [ResolutionTypes.Fall, ResolutionTypes.OtherEmergency],
    },
    {
        ...ResolutionTypeCategories.Others,
        subTypes: [
            ResolutionTypes.AccidentallyPressed,
            ResolutionTypes.NotFeelingWell,
            ResolutionTypes.OtherRequest,
        ],
    },
];

export const CareRefusalOptions: ResolutionTypeCategory[] = [
    {
        ...ResolutionTypeCategories.ADLHelp,
        subTypes: sortBy(
            [
                ResolutionTypes.Toileting,
                ResolutionTypes.EatingFeeding,
                ResolutionTypes.Bathing,
                ResolutionTypes.Dressing,
                ResolutionTypes.Grooming,
                ResolutionTypes.Transfer,
                ResolutionTypes.Escort,
            ],
            'sortOrder'
        ),
    },
    {
        ...ResolutionTypeCategories.Cleaning,
        subTypes: sortBy(
            [
                ResolutionTypes.MadeBed,
                ResolutionTypes.CleanedBathroom,
                ResolutionTypes.Laundry,
                ResolutionTypes.EmptiedTrash,
                ResolutionTypes.OtherCleaning,
            ],
            'sortOrder'
        ),
    },
];

export const SkilledNursingOptions: ResolutionTypeCategory[] = [
    {
        id: ResolutionTypeCategories.Request.id,
        sortOrder: ResolutionTypeCategories.Request.sortOrder,
        subTypes: [
            {
                ...ResolutionTypes.Medtech,
                displayName: 'Needs Nurse',
                icon: fak.faNurse,
                category: undefined,
            },
        ],
    },
    {
        ...ResolutionTypeCategories.Emergency,
        subTypes: [ResolutionTypes.Fall, ResolutionTypes.OtherEmergency],
    },
    {
        ...ResolutionTypeCategories.ADLHelp,
        subTypes: [
            ResolutionTypes.Toileting,
            ResolutionTypes.Dressing,
            ResolutionTypes.Grooming,
            {
                ...ResolutionTypes.Transfer,
                displayName: 'Transfer/Mobility',
            },
            {
                ...ResolutionTypes.EatingFeeding,
                displayName: 'Eating/Drinking',
            },
        ],
    },
    {
        ...ResolutionTypeCategories.Request,
        displayName: 'Other Help',
        subTypes: [
            ResolutionTypes.EmotionalSupport,
            ResolutionTypes.HousekeepingUnspecified,
            ResolutionTypes.Maintenance,
            ResolutionTypes.OtherRequest,
            ResolutionTypes.AccidentallyPressed,
        ],
    },
];

// These should be reconciled with ResolutionTypes eventually. Used just for planned care task outcomes in the Resident Care Page
export const PlannedCareResolutionTypes: Record<string, ResolutionType> = {
    MobilityAid: {
        icon: faPersonCane,
        id: 'ADL_MOBILITY_AID',
        displayName: 'Mobility Aid',
        sortOrder: 1,
        category: ResolutionTypeCategories.ADLHelp,
    },
    OtherADL: {
        icon: faHandHoldingHeart,
        id: 'ADL_OTHER',
        displayName: 'Other ADL',
        sortOrder: 1,
        category: ResolutionTypeCategories.ADLHelp,
    },
    SkinCare: {
        icon: faHandDots,
        id: 'ADL_SKIN_CARE',
        displayName: 'Skin Care',
        sortOrder: 1,
        category: ResolutionTypeCategories.ADLHelp,
    },
    CognitiveBehavioral: {
        icon: faHeadSideBrain,
        id: 'ADL_COGNITIVE_BEHAVIORAL',
        displayName: 'Cognitive/Behavioral Assistance',
        sortOrder: 1,
        category: ResolutionTypeCategories.ADLHelp,
    },
    ClinicalMonitoring: {
        icon: faHeartPulse,
        id: 'ADL_CLINICAL_MONITORING',
        displayName: 'Clinical Monitoring',
        sortOrder: 1,
        category: ResolutionTypeCategories.ADLHelp,
    },
    OralHygiene: {
        icon: faToothbrush,
        id: 'ADL_ORAL_HYGIENE',
        displayName: 'Oral Hygiene',
        sortOrder: 1,
        category: ResolutionTypeCategories.ADLHelp,
    },
    VisionAssistance: {
        icon: faGlasses,
        id: 'ADL_VISION_ASSISTANCE',
        displayName: 'Vision Assistance',
        sortOrder: 1,
        category: ResolutionTypeCategories.ADLHelp,
    },
    HearingAssistance: {
        icon: fak.faHearingAid,
        id: 'ADL_HEARING_ASSISTANCE',
        displayName: 'Hearing Assistance',
        sortOrder: 1,
        category: ResolutionTypeCategories.ADLHelp,
    },
    FallIntervention: {
        icon: fak.faFallPrevention,
        id: 'SAFETY_FALL_INTERVENTION',
        displayName: 'Fall Intervention',
        sortOrder: 1,
        category: ResolutionTypeCategories.Safety,
    },
    ElopementRiskManagement: {
        icon: faPersonRunningFast,
        id: 'SAFETY_ELOPEMENT_RISK_MANAGEMENT',
        displayName: 'Elopement Risk Management',
        sortOrder: 1,
        category: ResolutionTypeCategories.Safety,
    },
    OtherSafety: {
        icon: faShieldHeart,
        id: 'SAFETY_OTHER',
        displayName: 'Other Safety',
        sortOrder: 1,
        category: ResolutionTypeCategories.Safety,
    },
    MedicationAssistance: {
        icon: faCapsules,
        id: 'IADL_MEDICATION_ASSISTANCE',
        displayName: 'Medication Assistance',
        sortOrder: 1,
        category: ResolutionTypeCategories.IADLHelp,
    },
    ActivityAssistance: {
        icon: faChessKnight,
        id: 'IADL_ACTIVITY_ASSISTANCE',
        displayName: 'Activity Assistance',
        sortOrder: 1,
        category: ResolutionTypeCategories.IADLHelp,
    },
    TransportationAssistance: {
        icon: faVanShuttle,
        id: 'IADL_TRANSPORTATION_ASSISTANCE',
        displayName: 'Transportation Assistance',
        sortOrder: 1,
        category: ResolutionTypeCategories.IADLHelp,
    },
    MoneyManagementAssistance: {
        icon: faMoneyCheckDollarPen,
        id: 'IADL_FINANCIAL_ASSISTANCE',
        displayName: 'Money Management Assistance',
        sortOrder: 1,
        category: ResolutionTypeCategories.IADLHelp,
    },
    PetAssistance: {
        icon: faDog,
        id: 'IADL_PET_ASSISTANCE',
        displayName: 'Pet Assistance',
        sortOrder: 1,
        category: ResolutionTypeCategories.IADLHelp,
    },
    OtherIADL: {
        icon: faHandHoldingHeart,
        id: 'IADL_OTHER',
        displayName: 'Other IADL',
        sortOrder: 1,
        category: ResolutionTypeCategories.IADLHelp,
    },
};

export const TaskingExtraCareOutcomes: ResolutionTypeCategory[] = [
    {
        id: 'UNCATEGORIZED',
        displayName: undefined,
        sortOrder: 0,
        subTypes: [ResolutionTypes.RoutineCheck],
    },
    {
        ...ResolutionTypeCategories.ADLHelp,
        subTypes: sortBy(
            [
                ResolutionTypes.Toileting,
                ResolutionTypes.Escort,
                ResolutionTypes.Dressing,
                ResolutionTypes.Transfer,
                ResolutionTypes.EatingFeeding,
                ResolutionTypes.Bathing,
                ResolutionTypes.Grooming,
            ],
            'sortOrder'
        ),
    },
    {
        ...ResolutionTypeCategories.Request,
        subTypes: sortBy(
            [
                ResolutionTypes.Medtech,
                ResolutionTypes.Oxygen,
                ResolutionTypes.Repair,
                ResolutionTypes.OtherRequest,
            ],
            'sortOrder'
        ),
    },
    {
        ...ResolutionTypeCategories.Cleaning,
        subTypes: sortBy(
            [
                ResolutionTypes.MadeBed,
                ResolutionTypes.CleanedBathroom,
                ResolutionTypes.Laundry,
                ResolutionTypes.EmptiedTrash,
                ResolutionTypes.OtherCleaning,
            ],
            'sortOrder'
        ),
    },
    {
        ...ResolutionTypeCategories.Emergency,
        subTypes: [ResolutionTypes.Fall, ResolutionTypes.OtherEmergency],
    },
];

const AllResolutionTypes = {
    ...ResolutionTypes,
    ...PlannedCareResolutionTypes,
};
export const ResolutionTypesById: Record<string, ResolutionType> = {
    ...Object.values(AllResolutionTypes).reduce((acc, resolutionType) => {
        return { ...acc, [resolutionType.id]: resolutionType };
    }, {} as Record<string, ResolutionType>),
};

export const IdToResolutionTypeMap = new Map(
    Object.values(AllResolutionTypes).map((type) => [type.id, type])
);

export const EmergencyResolutionDisplayValues = [
    ResolutionTypes.Fall.displayName,
    ResolutionTypes.OtherEmergency.displayName,
];

/**
 * Display name shown for fall alerts when the resolving caregiver indicates "no fall".
 * Substituted in for the `NoCareGiven` resolution type when the alert came from a fall device.
 */
export const NO_FALL_DISPLAY_NAME = 'No Fall';
